/**
 * This file may contain code that utilizes generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { find, omit, pick } from 'lodash';

const DRIVER = 'DRIVER';

export const isVehicleTypeResolved = ({
  vehicleSetupLoaded,
  vehicleTypeSetupLoaded,
  vehicles,
  vehicleTypes,
  vehicleId,
}) => {
  if (!vehicleSetupLoaded || !vehicleTypeSetupLoaded) {
    return false;
  }

  if (!vehicleId) {
    return true;
  }

  const targetVehicle = find(vehicles, { id: vehicleId });

  if (!targetVehicle) {
    return true;
  }

  const vehicleTypeRef = targetVehicle?.vehicleType?.vehicleTypeRef;

  if (!vehicleTypeRef) {
    return true;
  }

  const targetVehicleType = find(vehicleTypes, { crn: vehicleTypeRef });

  return !!targetVehicleType;
};

export const isMasterDataReady = ({
  companySetupLoaded,
  productSetupLoaded,
  vehicleSetupLoaded,
  vehicleTypeSetupLoaded,
  vehicleTypeResolved,
}) => {
  return (
    companySetupLoaded && productSetupLoaded && vehicleSetupLoaded && vehicleTypeSetupLoaded && vehicleTypeResolved
  );
};

export const isVehicleWorkflowStepEnabled = ({
  companyEnabled,
  vehicleTypeData,
  vehicleTypeResolved,
  vehicleTypeFlag,
}) => {
  if (!companyEnabled) {
    return false;
  }

  if (!vehicleTypeResolved) {
    return false;
  }

  if (!vehicleTypeData) {
    return true;
  }

  return vehicleTypeData?.[vehicleTypeFlag] === true;
};

export const buildSetupMandatoryItems = ({ products, companySetup, ticketOriginCountryCode, vehicleType }) => {
  return products
    ?.filter?.(p => p?.isDriverSellable)
    ?.filter?.(product => product?.status === 'ACTIVE')
    ?.filter?.(product =>
      companySetup?.isMulticountry === true
        ? product?.countries?.includes(ticketOriginCountryCode) || !product?.countries?.length
        : product
    )
    ?.filter?.(product => product?.vehicleTypes?.includes(vehicleType) || !product?.vehicleTypes?.length)
    ?.filter?.(p => p?.mobileTicket?.mandatory === true);
};

export const buildFormattedMandatoryItems = setupMandatoryItems => {
  return setupMandatoryItems?.map?.(mandatoryItem => ({
    item: {
      ...pick(mandatoryItem, [
        'createDate',
        'name',
        'id',
        'isDriverSellable',
        'mobileTicket',
        'modifyDate',
        'productRef',
        'status',
        'type',
        'typeId',
        'uomCode',
        'i18n',
      ]),
      productRef: mandatoryItem?.crn,
    },
    quantity: {
      uomCode: mandatoryItem?.uomCode,
    },
    suggestedToDriver: true,
    source: DRIVER,
  }));
};

export const buildMandatoryWaterItem = ({ companySetup, products, vehicleTypeData, vehicleTypeResolved }) => {
  const isWaterEnabled = isVehicleWorkflowStepEnabled({
    companyEnabled: !!companySetup?.isWaterMandatory && !!companySetup?.isWaterInWorkflow,
    vehicleTypeData,
    vehicleTypeResolved,
    vehicleTypeFlag: 'isWaterInWorkflow',
  });

  if (!isWaterEnabled) {
    return null;
  }

  const defaultWaterRef = companySetup?.defaultWaterProduct?.productRef;
  const defaultWater = {
    ...omit(find(products, { crn: defaultWaterRef }), 'crn'),
    productRef: defaultWaterRef,
  };

  return {
    waterProduct: defaultWater,
    quantity: {
      uomCode: defaultWater?.uomCode,
    },
    suggestedToDriver: true,
  };
};

export const buildMandatoryLineItems = ({
  ticketLineItems,
  formattedMandatoryItems,
  companySetup,
  vehicleTypeData,
  vehicleTypeResolved,
}) => {
  const isMaterialServiceEnabled = isVehicleWorkflowStepEnabled({
    companyEnabled: !!companySetup?.isMaterialServiceInWorkflow,
    vehicleTypeData,
    vehicleTypeResolved,
    vehicleTypeFlag: 'isMaterialServiceInWorkflow',
  });

  if (!isMaterialServiceEnabled || !formattedMandatoryItems?.length) {
    return [];
  }

  const upFront = ticketLineItems
    ?.filter?.(item => !item?.isPrimary)
    ?.filter?.(item => !item?.isSplitLoad)
    ?.filter?.(item => item?.requiresDriverReview);

  const mandatoryItems = [];

  for (let i = 0; i < (formattedMandatoryItems?.length ?? 0); i++) {
    const existing = upFront?.find?.(o => o?.item?.id === formattedMandatoryItems?.[i]?.item?.id);

    if (!existing) {
      mandatoryItems.push(formattedMandatoryItems?.[i]);
    }
  }

  return mandatoryItems;
};
